import React, { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Header } from "../components/Sidebar";
import api, { apiErr } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Building2, MapPin, Plus, Power, Store, MessageCircle, X } from "lucide-react";
import { toast } from "sonner";

const input = "w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-blue-100";

function autoCode(ciudad) {
  const clean = (ciudad || "").toUpperCase().replace(/[^A-ZÁÉÍÓÚÑ ]/g, "").trim();
  const words = clean.split(/\s+/).filter(Boolean);
  if (!words.length) return "";
  if (words.length === 1) return words[0].slice(0, 3);
  return words.slice(0, 3).map((w) => w[0]).join("");
}

export default function Sucursales() {
  const { setMobileOpen } = useOutletContext();
  const [sucursales, setSucursales] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ ciudad: "", name: "", code: "", ciudades: "", es_punto: false, whatsapp: "" });
  const [saving, setSaving] = useState(false);
  const [editWa, setEditWa] = useState(null); // {code, whatsapp, ciudades:[], nueva:""}

  const load = useCallback(() => { api.get("/sucursales").then(({ data }) => setSucursales(data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const onCiudad = (v) => setForm((f) => ({ ...f, ciudad: v, name: f.name || v, code: f.code || autoCode(v) }));

  const crear = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/sucursales", {
        code: form.code.trim().toUpperCase(), name: form.name.trim(), ciudad: form.ciudad.trim(),
        ciudades: form.ciudades.split(",").map((c) => c.trim()).filter(Boolean),
        es_punto: form.es_punto, whatsapp: form.whatsapp.trim(),
      });
      toast.success(`Sucursal ${form.code.toUpperCase()} creada`);
      setShowCreate(false);
      setForm({ ciudad: "", name: "", code: "", ciudades: "", es_punto: false, whatsapp: "" });
      load();
    } catch (e2) { toast.error(apiErr(e2)); }
    finally { setSaving(false); }
  };

  const guardarWa = async () => {
    try { await api.patch(`/sucursales/${editWa.code}`, { whatsapp: (editWa.whatsapp || "").trim(), ciudades: editWa.ciudades || [] }); toast.success("Sucursal actualizada"); setEditWa(null); load(); }
    catch (e2) { toast.error(apiErr(e2)); }
  };

  const toggle = async (s) => {
    try { await api.patch(`/sucursales/${s.code}`, { activa: !s.activa }); toast.success(`${s.code} ${s.activa ? "desactivada" : "activada"}`); load(); }
    catch (e2) { toast.error(apiErr(e2)); }
  };

  return (
    <>
      <Header title="Sucursales" subtitle="Red de sucursales y puntos Pya'elino" onMenu={() => setMobileOpen(true)}
        action={<button onClick={() => setShowCreate(true)} data-testid="crear-sucursal-btn" className="flex items-center gap-1.5 rounded-lg bg-[#0A1F44] text-white px-3.5 py-2 text-sm font-bold hover:bg-[#132D5C]"><Plus className="h-4 w-4" /> Crear sucursal</button>} />
      <main className="p-4 sm:p-6 lg:p-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {sucursales.map((s) => (
            <div key={s.code} className={`bg-white rounded-xl border p-5 shadow-sm ${s.activa === false ? "border-rose-200 opacity-70" : "border-[#E2E8F0]"}`} data-testid={`sucursal-${s.code}`}>
              <div className="flex items-center justify-between">
                <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${s.es_punto ? "bg-[#F5A623]" : "bg-[#0A1F44]"}`}>{s.es_punto ? <Store className="h-5 w-5 text-white" /> : <Building2 className="h-5 w-5 text-[#F5A623]" />}</div>
                <span className="font-mono font-bold text-sm px-2.5 py-1 rounded-lg bg-blue-50 text-[#0A1F44] border border-blue-100">{s.code}</span>
              </div>
              <div className="mt-3 font-bold text-lg text-[#0A1F44] font-display">{s.name}</div>
              <div className="flex items-center gap-1.5 text-sm text-[#64748B] mt-1"><MapPin className="h-4 w-4" /> {s.ciudad}, Paraguay</div>
              {s.es_punto && <div className="mt-1 text-xs font-semibold text-[#F5A623]">Punto Pya'elino · solo retiro</div>}
              {s.ciudades?.length > 1 && <div className="mt-2 text-[11px] text-[#64748B]">Cubre: {s.ciudades.slice(0, 5).join(", ")}{s.ciudades.length > 5 ? "…" : ""}</div>}
              <div className="mt-2 flex items-center gap-1.5 text-xs text-[#64748B]" data-testid={`suc-wa-${s.code}`}>
                <MessageCircle className="h-3.5 w-3.5 text-emerald-500" />
                {s.whatsapp ? <span className="font-medium text-[#0A1F44]">{s.whatsapp}</span> : <span className="text-rose-500 font-medium">Sin WhatsApp (retiros no llegan)</span>}
                <button onClick={() => setEditWa({ code: s.code, whatsapp: s.whatsapp || "", ciudades: s.ciudades || [], nueva: "" })} data-testid={`edit-wa-${s.code}`} className="ml-1 text-[#3B82F6] font-semibold hover:underline">Editar</button>
              </div>
              <div className="mt-4 flex items-center justify-between">
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${s.activa === false ? "bg-rose-50 text-rose-600" : "bg-emerald-50 text-emerald-700"}`}>{s.activa === false ? "Inactiva" : "Activa"}</span>
                <button onClick={() => toggle(s)} data-testid={`toggle-sucursal-${s.code}`} className={`flex items-center gap-1.5 text-xs font-semibold rounded-lg px-3 py-1.5 border ${s.activa === false ? "text-emerald-700 border-emerald-300 hover:bg-emerald-50" : "text-rose-600 border-rose-300 hover:bg-rose-50"}`}><Power className="h-3.5 w-3.5" /> {s.activa === false ? "Activar" : "Desactivar"}</button>
              </div>
            </div>
          ))}
        </div>
      </main>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display">Crear sucursal</DialogTitle><DialogDescription>Nueva sucursal o Punto Pya'elino en otra ciudad</DialogDescription></DialogHeader>
          <form onSubmit={crear} className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-[#64748B]">Ciudad principal</label>
              <input className={input} placeholder="Ej. Presidente Franco" value={form.ciudad} onChange={(e) => onCiudad(e.target.value)} required data-testid="suc-ciudad-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-[#64748B]">Código</label>
                <input className={`${input} font-mono uppercase`} placeholder="PTE" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} maxLength={5} required data-testid="suc-code-input" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#64748B]">Nombre</label>
                <input className={input} placeholder="Presidente Franco" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="suc-name-input" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#64748B]">Ciudades que cubre (separadas por coma)</label>
              <input className={input} placeholder="Presidente Franco, Domingo Martínez" value={form.ciudades} onChange={(e) => setForm({ ...form, ciudades: e.target.value })} data-testid="suc-ciudades-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#64748B]">WhatsApp de la sucursal (recibe solicitudes de retiro)</label>
              <input className={input} placeholder="Ej. 0981123456" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} data-testid="suc-whatsapp-input" />
            </div>
            <label className="flex items-center gap-2 text-sm font-semibold text-[#0A1F44] bg-slate-50 border border-slate-200 rounded-lg p-3 cursor-pointer">
              <input type="checkbox" checked={form.es_punto} onChange={(e) => setForm({ ...form, es_punto: e.target.checked })} data-testid="suc-espunto-check" className="h-4 w-4 rounded border-slate-300 accent-[#F5A623]" />
              Es Punto Pya'elino (solo retiro, sin entrega a domicilio)
            </label>
            <button disabled={saving} data-testid="suc-submit-btn" className="w-full rounded-lg bg-[#0A1F44] text-white font-bold py-3 text-sm disabled:opacity-60">{saving ? "Creando..." : "Crear sucursal"}</button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editWa} onOpenChange={(o) => { if (!o) setEditWa(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display">Editar sucursal · {editWa?.code}</DialogTitle><DialogDescription>WhatsApp de solicitudes y ciudades de cobertura</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-[#64748B]">WhatsApp (recibe solicitudes de retiro)</label>
              <input className={input} placeholder="Ej. 0981123456" value={editWa?.whatsapp || ""} onChange={(e) => setEditWa({ ...editWa, whatsapp: e.target.value })} data-testid="edit-wa-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#64748B]">Ciudades que cubre</label>
              <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="edit-ciudades-chips">
                {(editWa?.ciudades || []).map((c, i) => (
                  <span key={i} className="flex items-center gap-1 text-xs font-medium bg-blue-50 text-[#0A1F44] border border-blue-100 rounded-full pl-2.5 pr-1 py-1">
                    {c}
                    <button onClick={() => setEditWa({ ...editWa, ciudades: editWa.ciudades.filter((_, j) => j !== i) })} data-testid={`quitar-ciudad-${i}`} className="hover:bg-rose-100 rounded-full p-0.5"><X className="h-3 w-3 text-rose-500" /></button>
                  </span>
                ))}
                {(editWa?.ciudades || []).length === 0 && <span className="text-xs text-[#64748B]">Sin ciudades adicionales</span>}
              </div>
              <div className="flex gap-2 mt-2">
                <input className={input} placeholder="Agregar ciudad" value={editWa?.nueva || ""} onChange={(e) => setEditWa({ ...editWa, nueva: e.target.value })}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); const v = (editWa.nueva || "").trim(); if (v && !editWa.ciudades.includes(v)) setEditWa({ ...editWa, ciudades: [...editWa.ciudades, v], nueva: "" }); } }}
                  data-testid="agregar-ciudad-input" />
                <button type="button" onClick={() => { const v = (editWa.nueva || "").trim(); if (v && !editWa.ciudades.includes(v)) setEditWa({ ...editWa, ciudades: [...editWa.ciudades, v], nueva: "" }); }} data-testid="agregar-ciudad-btn" className="rounded-lg bg-[#0A1F44] text-white px-3 text-sm font-semibold shrink-0">Agregar</button>
              </div>
            </div>
            <button onClick={guardarWa} data-testid="edit-wa-save-btn" className="w-full rounded-lg bg-[#0A1F44] text-white font-bold py-2.5 text-sm">Guardar</button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
